import { AvailableRoomModel, HotelInfoModel } from '@/__generated';
import { scrollToElementById } from '@/helpers/utils';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { minBy, min, sumBy } from 'lodash';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { ISelectedRoomTypes } from '../type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import SingleOrPluralText from '@/components/App/SingleOrPluralText';
import { useGetUseRole } from '@/hooks/useGetUserRole';

type Props = {
  hotelInfo: HotelInfoModel;
  searchRoomLoading?: boolean;
  availableRooms: AvailableRoomModel[] | null | undefined;
  selectedRooms: ISelectedRoomTypes[];
  preBook: () => void;
};

export default function StickyHeader({ hotelInfo, searchRoomLoading, availableRooms, selectedRooms, preBook }: Props) {
  const { formatCurrency } = useCurrencyFormat();
  const [header, setHeader] = React.useState(false);
  const { isAdmin, isBooking } = useGetUseRole();
  const minPrice = React.useMemo(() => {
    if (availableRooms?.length) {
      try {
        let _minPrice: number = -1;
        availableRooms.forEach(room =>
          room.availableRoomTypes?.forEach(types => {
            let minPlanPrice = minBy(types.roomPlans, plan => plan.price || 0)?.price || 0;
            if (_minPrice === -1) {
              _minPrice = minPlanPrice;
            } else {
              _minPrice = min([_minPrice, minPlanPrice]) || 0;
            }
          }),
        );
        return _minPrice;
      } catch (error) {
        return 0;
      }
    }
    return 0;
  }, [availableRooms]);

  const totalSelectedPrice = React.useMemo(() => {
    var total = 0;
    selectedRooms?.forEach(availableRooms => {
      availableRooms.roomTypes.forEach(rooms => {
        total += sumBy(rooms.plans, x => x.price * x.selectedRooms) || 0;
      });
    });
    return total;
  }, [selectedRooms]);
  const scrollToElement = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, id: string) => {
    scrollToElementById(event, id);
  };

  const changeBackground = () => {
    if (window.innerWidth > 1300) {
      if (window.scrollY >= 200) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    }
  };

  React.useEffect(() => {
    window.addEventListener('scroll', changeBackground);
  }, []);

  return (
    <div className={`transition-cubic ${header ? 'singleMenu js-singleMenu -is-active bg-dark-2 text-white' : ''}`}>
      <div className="container">
        <div className="row  justify-between items-center py-10">
          <div className="col-auto">
            <div className="singleMenu__links row x-gap-30 y-gap-10">
              <div className="col-auto">
                <a href="#overview" onClick={e => scrollToElement(e, 'overview')}>
                  Overview
                </a>
              </div>
              <div className="col-auto">
                <a href="#facilities" onClick={e => scrollToElement(e, 'facilities')}>
                  Amenities and facilities
                </a>
              </div>
              {hotelInfo.hotelBaths?.length ? (
                <div className="col-auto">
                  <a href="#spa" onClick={e => scrollToElement(e, 'spa')}>
                    Spa (Onsen/ Public bath)
                  </a>
                </div>
              ) : null}
              {hotelInfo.hotelPools?.length ? (
                <div className="col-auto">
                  <a href="#swimming-pool" onClick={e => scrollToElement(e, 'swimming-pool')}>
                    Swimming Pool
                  </a>
                </div>
              ) : null}

              <div className="col-auto">
                <a href="#rooms" onClick={e => scrollToElement(e, 'rooms')}>
                  Rooms
                </a>
              </div>
              <div className="col-auto">
                <a href="#hotel-buildings" onClick={e => scrollToElement(e, 'hotel-buildings')}>
                  Buildings
                </a>
              </div>
            </div>
          </div>
          {/* End .col */}

          <div className="col-auto">
            {searchRoomLoading ? (
              <Skeleton height={50} width={150} />
            ) : totalSelectedPrice ? (
              <div className="row x-gap-15 y-gap-15 items-center">
                <UncontrolledDropdown tag="div" className="col-auto">
                  <DropdownToggle tag="button" className={`text-14 button ${header ? 'text-white' : 'text-blue-1'}`}>
                    <span className="text-22  fw-500 ">{formatCurrency(totalSelectedPrice)}</span>
                    <FontAwesomeIcon icon={faChevronDown} className="ml-5" />
                  </DropdownToggle>
                  <DropdownMenu tag="div" className="search-box-transition nta-map-popover shadow-2 p-3" placement="bottom">
                    {selectedRooms.map((selectedRoom, roomIndex) => {
                      return (
                        <div key={`${selectedRoom.noOfAdult}-${selectedRoom.noOfChild}-${roomIndex}`}>
                          <div className="text-blue-1 fw-500">#Room: 2 adult</div>
                          <div className="ml-10">
                            {selectedRoom.roomTypes.map((room, index) => {
                              return (
                                <div key={`${room.category}-${room.roomTypeId}-${index}`}>
                                  <div className="fw-500 text-14">{`[${room.category}] - ${room.name}`}</div>
                                  <div className="ml-10 text-14">
                                    {room.plans.map(plan => {
                                      return (
                                        <div className="row x-gap-5 y-gap-5" key={plan.roomPlanId}>
                                          <div className="col-10 d-flex justify-content-between">
                                            {`${plan.name}`}
                                            <span className="ml-5 fw-500">
                                              <SingleOrPluralText count={plan.selectedRooms} singleText="x{} room" pluralText="x{} rooms" />
                                            </span>
                                          </div>
                                          <div className="col-2 fw-500 text-end">{formatCurrency(plan.price)}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <div className=" mt-10 pt-10  fw-500  border-top-light">
                      <div className="row x-gap-5 y-gap-5">
                        <div className="col">Price summary</div>
                        <div className="col-auto fw-500 text-end">{formatCurrency(totalSelectedPrice)}</div>
                      </div>
                    </div>
                  </DropdownMenu>
                </UncontrolledDropdown>
                <div className="col-auto">
                  {isAdmin || isBooking ? (
                    <button className="button h-50 px-24 -dark-1 bg-blue-1 text-white" onClick={preBook}>
                      Reserve <div className="icon-arrow-top-right ml-15" />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="row x-gap-15 y-gap-15 items-center">
                <div className="col-auto">
                  <div className="text-14">
                    From <span className="text-22  fw-500">{formatCurrency(minPrice)}</span>
                  </div>
                </div>
                <div className="col-auto">
                  <button className="button h-50 px-24 -dark-1 bg-blue-1 text-white" onClick={e => scrollToElement(e as any, 'rooms')}>
                    Select Room <FontAwesomeIcon icon={faChevronDown} className="ml-15" />
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* End .col */}
        </div>
      </div>
    </div>
  );
}
